import { apiPContactsList } from 'modules/Api'
import { contactFromSearch } from 'modules/Contact'
import CONTACTS from 'constants/Contacts'
import * as C from './constants'
import {
  setSyncToken,
  syncContactsWithIndexDb,
  getSyncToken,
  getAllIndexDbContacts,
} from './helpers'

const { department, guest, teamMember, welcomeMenu, ringGroup, callQueues } =
  CONTACTS.names

const getContactType = (type) => {
  return (
    {
      user: teamMember,
      ring_group: ringGroup,
      call_queue: callQueues,
      welcome_menu: welcomeMenu,
      department,
    }[type] || guest
  )
}

const contactsResponseAdapter = (response) => {
  return (
    response.items
      // We didn't have special_extension in the past, so we need to filter it out
      .filter((contact) =>
        contact.pbx_route?.type
          ? contact.pbx_route?.type !== 'special_extension'
          : true,
      )
      .map((contact) => {
        return {
          id: contact.pbx_route?.id || contact.id?.split(':')?.[1],
          name: contact.names?.[0]?.display_name,
          email: contact.emails?.[0],
          type: getContactType(contact.pbx_route?.type),
          extension: contact.extension?.[0]?.id,
          status: contact.status,
          phone: contact.phones?.[0]?.phone_number_e164_canonical_form,
          phones: contact.phones?.map(
            (phone) => phone?.phone_number_e164_canonical_form,
          ),
          avatar: contact.avatars?.[0]?.url,
        }
      })
  )
}

export const fetchAllContacts = () => async (dispatch) => {
  const token = await getSyncToken()

  try {
    dispatch({ type: C.GUESTS_FETCH_PENDING })
    dispatch({ type: C.TEAM_MEMBERS_FETCH_PENDING })
    dispatch({ type: C.RING_GROUPS_FETCH_PENDING })
    dispatch({ type: C.DEPARTMENTS_FETCH_PENDING })
    dispatch({ type: C.WELCOME_MENUS_FETCH_PENDING })
    dispatch({ type: C.CALL_QUEUES_FETCH_PENDING })

    const contactsDiff = await apiPContactsList(token)

    await setSyncToken(contactsDiff.next_sync_token ?? '')
    await syncContactsWithIndexDb(contactsResponseAdapter(contactsDiff))

    const contacts = await getAllIndexDbContacts()

    const result = contacts.reduce(
      // eslint-disable-next-line array-callback-return
      (acc, contact) => {
        if (contact.type in acc) {
          acc[contact.type].push(contact)
        } else {
          // eslint-disable-next-line no-param-reassign
          acc[contact.type] = [contact]
        }

        return acc
      },
      {},
    )

    Object.keys(result).forEach((key) => {
      if (key.includes('welcome_menu')) {
        dispatch({
          type: C.WELCOME_MENUS_FETCH_FULFILLED,
          payload: result[key],
        })
      } else if (key.includes(callQueues)) {
        dispatch({ type: C.CALL_QUEUES_FETCH_FULFILLED, payload: result[key] })
      } else if (key.includes(ringGroup)) {
        dispatch({ type: C.RING_GROUPS_FETCH_FULFILLED, payload: result[key] })
      } else if (key.includes(department)) {
        dispatch({ type: C.DEPARTMENTS_FETCH_FULFILLED, payload: result[key] })
      } else if (key.includes(teamMember)) {
        dispatch({ type: C.TEAM_MEMBERS_FETCH_FULFILLED, payload: result[key] })
      } else if (key.includes(welcomeMenu)) {
        dispatch({
          type: C.WELCOME_MENUS_FETCH_FULFILLED,
          payload: result[key],
        })
      } else if (key.includes(guest)) {
        dispatch({ type: C.GUESTS_FETCH_FULFILLED, payload: result[key] })
      } else {
        dispatch({ type: C.GUESTS_FETCH_FULFILLED, payload: result[key] })
      }
    })
  } catch {
    dispatch({ type: C.GUESTS_FETCH_REJECTED })
    dispatch({ type: C.TEAM_MEMBERS_FETCH_REJECTED })
    dispatch({ type: C.RING_GROUPS_FETCH_REJECTED })
    dispatch({ type: C.DEPARTMENTS_FETCH_REJECTED })
    dispatch({ type: C.WELCOME_MENUS_FETCH_REJECTED })
    dispatch({ type: C.CALL_QUEUES_FETCH_REJECTED })
  }
}

export const searchClear = () => (dispatch, getState) => {
  if (getState().contacts.search) {
    dispatch({ type: C.SEARCH_CLEAR })
  }
}

export const searchContacts = (search) => ({
  type: C.SEARCH,
  payload: search,
  meta: {
    search: true,
  },
})

export const searchContactsFulfilled = (payload) => ({
  type: C.SEARCH_FULFILLED,
  payload,
})

export const searchKeyPending =
  (key, append = false) =>
  (dispatch, getState) => {
    let { search } = getState().contacts

    if (append) {
      search += key
    }

    dispatch({
      type: C.SEARCH_KEY_PENDING,
      payload: search,
      meta: {
        key,
        search: append,
      },
    })
  }

export const searchKeyFulfilled =
  (replace = false) =>
  (dispatch, getState) => {
    let { search } = getState().contacts
    let update = false

    if (replace && !!search.length && search.substr(-1) === '0') {
      search = `${search.slice(0, -1)}+`
      update = true
    }

    dispatch({
      type: C.SEARCH_KEY_FULFILLED,
      payload: search,
      meta: {
        search: update,
      },
    })
  }

export const contactSelectedSet = (contact, clear = true) => ({
  type: C.SELECTED_SET,
  payload: {
    contact,
    clear,
  },
})

export const contactSelectedFromInput = () => (dispatch, getState) => {
  if (getState().contacts.search && !getState().contacts.selected) {
    dispatch(contactSelectedSet(contactFromSearch()))
  }
}

export const contactSelectedClear =
  (clear = true) =>
  (dispatch, getState) => {
    if (getState().contacts.selected) {
      dispatch({ type: C.SELECTED_CLEAR, payload: clear })
    }
  }

export const selectedTypeSet = (type) => ({
  type: C.SELECTED_TYPE_SET,
  payload: type,
})

export const selectedTypeClear = () => ({
  type: C.SELECTED_TYPE_CLEAR,
})
